import {keepPreviousData, useMutation, useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {useSnackBar} from "@hooks/alert/useSnackBar";
import {buildErrorsMessages} from "@ui/errors/helpers/helpers";
import {queryClient} from "@app/appProvider/queryClient/queryClient";
import {
	connectToAccountApi,
	createAdminAccountApi,
	getAdminAccountApi,
	getAdminAccountsApi,
	getSwitchableAccountsApi,
	getSwitchableUsersApi,
	setFavoriteAccountApi,
	releaseImpersonationApi,
	updateAdminAccountApi
} from "@containers/adminAccounts/api/api";
import {switchToTokens, trackPathAfterSwitch} from "@containers/adminAccounts/helpers/impersonation";
import {
	IAdminAccountDetails,
	IConnectTarget,
	ISwitchableAccount,
	ISwitchableAccounts
} from "@containers/adminAccounts/types";

export const QUERY_KEY_ADMIN_ACCOUNTS = 'admin-accounts'
export const QUERY_KEY_ADMIN_ACCOUNT = 'admin-account'
export const QUERY_KEY_SWITCHABLE_ACCOUNTS = 'switchable-accounts'
export const QUERY_KEY_SWITCHABLE_USERS = 'switchable-users'

export const useGetAdminAccountsQuery = () => {
	return useQuery({
		queryFn: getAdminAccountsApi,
		queryKey: [QUERY_KEY_ADMIN_ACCOUNTS],
		staleTime: 1000 * 60,
		placeholderData: keepPreviousData,
	});
}

export const useGetAdminAccountQuery = ({id, enabled = true}: { id: number, enabled?: boolean }) => {
	return useQuery({
		queryFn: () => getAdminAccountApi(id),
		queryKey: [QUERY_KEY_ADMIN_ACCOUNT, id],
		enabled: enabled && !!id,
		// The form starts from what is stored now, not from an earlier visit.
		staleTime: 0,
		refetchOnWindowFocus: false,
	});
}

export const useUpdateAdminAccountQuery = ({onSuccess}: { onSuccess?: (details: IAdminAccountDetails) => void } = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: updateAdminAccountApi,
		onSuccess: (details) => {
			queryClient.invalidateQueries({queryKey: [QUERY_KEY_ADMIN_ACCOUNTS]})
			queryClient.setQueryData([QUERY_KEY_ADMIN_ACCOUNT, details.id], details)
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'success'
			})
			onSuccess && onSuccess(details)
		},
		onError: (e) => {
			setSnackBar({
				message: buildErrorsMessages(e),
				variant: 'error'
			})
		}
	});
}

export const useCreateAdminAccountQuery = ({onSuccess}: { onSuccess: (details: IAdminAccountDetails) => void }) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: createAdminAccountApi,
		onSuccess: (details) => {
			queryClient.invalidateQueries({queryKey: [QUERY_KEY_ADMIN_ACCOUNTS]})
			queryClient.setQueryData([QUERY_KEY_ADMIN_ACCOUNT, details.id], details)
			setSnackBar({title: t('adminAccounts.created'), variant: 'success'})
			onSuccess(details)
		},
		onError: (e) => {
			setSnackBar({message: buildErrorsMessages(e), variant: 'error'})
		}
	});
}

export const useConnectToAccountQuery = () => {
	const {setSnackBar} = useSnackBar()

	return useMutation({
		mutationFn: ({code, login}: Pick<IConnectTarget, 'code' | 'login'>) => connectToAccountApi({accountCode: code, login}),
		onSuccess: (tokens, {login}) => {
			if (tokens?.token) {
				// A user lands on their own start page, the administrator on the account page.
				switchToTokens(tokens, login ? '' : '/account/info')
			}
		},
		onError: (e) => {
			setSnackBar({
				message: buildErrorsMessages(e),
				variant: 'error'
			})
		}
	});
}

export const useSwitchableAccountsQuery = ({enabled}: { enabled: boolean }) => {
	return useQuery({
		queryFn: getSwitchableAccountsApi,
		queryKey: [QUERY_KEY_SWITCHABLE_ACCOUNTS],
		enabled,
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
		// A refusal only hides the switcher.
		retry: false,
	});
}

export const useSwitchableUsersQuery = (accountId: number | undefined) => {
	return useQuery({
		queryFn: () => getSwitchableUsersApi(accountId as number),
		queryKey: [QUERY_KEY_SWITCHABLE_USERS, accountId],
		enabled: !!accountId,
		staleTime: 1000 * 60,
		refetchOnWindowFocus: false,
	});
}

const setFavorite = (id: number, favorite: boolean) =>
	queryClient.setQueryData<ISwitchableAccounts>([QUERY_KEY_SWITCHABLE_ACCOUNTS], (data) => data && {
		...data,
		accounts: data.accounts.map((account) => account.id === id ? {...account, favorite} : account),
	})

/** The star shows the change at once and goes back if the server refuses it. */
export const useFavoriteAccountQuery = () => {
	const {setSnackBar} = useSnackBar()

	return useMutation({
		mutationFn: setFavoriteAccountApi,
		onMutate: ({id, favorite}) => setFavorite(id, favorite),
		onError: (e, {id, favorite}) => {
			setFavorite(id, !favorite)
			setSnackBar({
				message: buildErrorsMessages(e),
				variant: 'error'
			})
		}
	});
}

/** Moves to another account of the tenant and stays on the same page where it makes sense there. */
export const useSwitchAccountQuery = () => {
	const {setSnackBar} = useSnackBar()

	return useMutation({
		mutationFn: (account: ISwitchableAccount) => account.own
			? releaseImpersonationApi()
			: connectToAccountApi({accountCode: account.code}),
		onSuccess: (tokens, account) => {
			if (tokens?.token) {
				switchToTokens(tokens, trackPathAfterSwitch(account.own))
			}
		},
		onError: (e) => {
			setSnackBar({
				message: buildErrorsMessages(e),
				variant: 'error'
			})
		}
	});
}
